import * as React from "react";
import { useLanguage } from "./Language";
// import { DevTool } from "@hookform/devtools";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { NotificationPreferences } from "./pages/account/NotificationPreferences";
import axios from "axios";
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  Path,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import {
  ObrConfiguration,
  ObrFormFieldUsage,
  ObrPinGeneration,
  SMSGateway,
} from "./OBRSettings";
import { BorrowerFieldControl } from "./pages/obr/BorrowerField";
import { PatronWithFields } from "./pages/CreateAccountPage";
import { HomeLocation } from "./pages/obr/HomeLocation";
import { PinField } from "./pages/obr/PinField";
import { PhoneField } from "./pages/obr/Phone";
import { GenderField } from "./pages/obr/GenderField";
import { DateOfBirthField } from "./pages/obr/DateOfBirthField";
import { FormTextField } from "./FormTextField";
import { useAccount } from "./Account";
import { OBRSuccess } from "./pages/obr/OBRSuccess";
import { Gender } from "./AccountSummaryAlerts";
import { useLocalizationContext } from "@mui/x-date-pickers/internals";
import { FailureReason, ObrResponse } from "./ObrResponse";
import { TermsAndConditions } from "./pages/obr/TermsAndConditions";
import { SignUpWithGoogle } from "./pages/obr/SignUpWithGoogle";
import { LinkedGoogleAccount } from "./LinkedGoogleAccount";
import { useSearchParams } from "react-router-dom";
import { AdditionalEmailGroup } from "./pages/obr/AdditionalEmailGroup";
import { PrimaryEmail } from "./pages/obr/PrimaryEmail";
import { AddressFieldGroup } from "./pages/obr/AddressFieldGroup";
import { PrimaryPhone } from "./pages/obr/PrimaryPhone";
import { MobilePhone } from "./pages/obr/MobilePhone";

export const ObrUrl = () => {
  // ILSFP-6249
  return (
    <Box sx={{ overflow: "hidden", width: 0, height: 0 }}>
      <label>
        Assistive technology users, please skip this field{" "}
        <input
          name="url"
          id="obrUrl"
          type="text"
          autoComplete={"off"}
          tabIndex={32766}
        />
      </label>
    </Box>
  );
};

export interface OBRFormInput extends PatronWithFields {
  confirmPIN: string;
  confirmEmail: string;
  agreeToTAC: boolean;
  url: string;
}
interface OBRFormProps {
  obrConfiguration: ObrConfiguration;
  smsGateways: SMSGateway[];
  defaultValues: DefaultValues<OBRFormInput>;
}

export const OBRForm: React.FC<OBRFormProps> = ({
  obrConfiguration,
  smsGateways,
  defaultValues: passedDefaults,
}) => {
  const { isAuthenticated } = useAccount();
  const adapter = useLocalizationContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const { translate, languageCode } = useLanguage();

  const termsAndConditions: string = React.useMemo(() => {
    return obrConfiguration.termsAndConditions[languageCode];
  }, [obrConfiguration, languageCode]);

  const generalMessage: { __html: string | TrustedHTML } = React.useMemo(() => {
    const message = obrConfiguration.generalMessage[languageCode];
    if (!message) {
      return null;
    }
    return {
      __html: message,
    };
  }, [obrConfiguration, languageCode]);

  const requiredFields: Map<string, boolean> = React.useMemo(() => {
    const required = new Map();
    obrConfiguration.obrFormFields.forEach((field) => {
      required.set(field.fieldName, field.required);
    });
    return required;
  }, [obrConfiguration]);

  const visibleFields: Map<string, boolean> = React.useMemo(() => {
    const hidden = new Map();
    obrConfiguration.obrFormFields.forEach((field) => {
      hidden.set(field.fieldName, field.visible);
    });
    return hidden;
  }, [obrConfiguration]);

  const defaultValues = React.useMemo(() => {
    console.log("recalculating defaults");
    const defaults: DefaultValues<OBRFormInput> = {
      fields: {},
      patron: {
        ...passedDefaults?.patron,
        issuingBranch: passedDefaults?.patron?.issuingBranch || null,
        gender: passedDefaults?.patron?.gender || Gender.Unspecified,
        dob: null,
        smsGatewayId: null,
      },
      confirmEmail: passedDefaults?.patron?.email || undefined,
      agreeToTAC: false,
    };

    obrConfiguration.borrowerFields
      .filter(
        (borrowerField) => borrowerField.obrUsage !== ObrFormFieldUsage.Hidden
      )
      .forEach(
        (borrowerField) =>
          (defaults.fields[borrowerField.fieldNumber] =
            passedDefaults?.fields?.[borrowerField.fieldNumber] ||
            borrowerField.defaultValue ||
            "")
      );
    return defaults;
  }, [obrConfiguration]);

  const formMethods = useForm<OBRFormInput>({
    defaultValues: defaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const { handleSubmit, watch, setValue, resetField } = formMethods;

  const issuingBranch = watch("patron.issuingBranch");

  const googleId = watch("patron.googleId");
  const googleEmail = watch("patron.googleEmail");
  const notificationTypes = [
    watch("patron.arrivedHoldNotificationTypes"),
    watch("patron.automaticRenewalNotificationTypes"),
    watch("patron.cancelledHoldNotificationTypes"),
    watch("patron.overdueNotificationTypes"),
    watch("patron.expiredCardNotificationTypes"),
    watch("patron.finalOverdueNotificationTypes"),
    watch("patron.friendlyWarningNotificationTypes"),
  ];

  const [success, setSuccess] = React.useState<ObrResponse>(null);
  const [error, setError] = React.useState<string>(null);

  const removeEmptyValues = (obj: { [key: string]: any }) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== null && value !== "")
    );
  };

  const onSubmit: SubmitHandler<OBRFormInput> = async ({
    patron,
    fields,
    ...otherData
  }) => {
    // ILSFP-6249
    const url = (document.getElementById("obrUrl") as HTMLInputElement)?.value;
    if (url) {
      return;
    }
    setError(null); // clear error between attempts
    // clear PIN in between attempts
    resetField("confirmPIN");
    resetField("patron.pin");

    if (!patron.gender) {
      patron.gender = Gender.Unspecified;
    }
    if (patron.dob) {
      // Format the DayJS object as a string
      const formattedDate = adapter.utils.format(patron.dob, "keyboardDate");

      // Remove the `/` characters
      patron.dob = formattedDate.replace(/\//g, "");
    }
    const payload: PatronWithFields = {
      patron,
      fields: removeEmptyValues(fields),
    };

    try {
      const { data } = await axios.post<ObrResponse>(
        `account/add?sendEmail=${obrConfiguration.sendEmailConfirmation}`,
        payload
      );

      const {
        response: {
          status: { success },
          failureReason,
        },
      } = data;
      if (success) {
        setSuccess(data); // show success component
      } else if (failureReason === FailureReason.Exists) {
        setError(
          `${translate("obrDuplicateBorrower")} ${translate(
            "obrContactLibrarian"
          )}`
        );
      } else {
        console.error(failureReason);
        setError(
          `${translate("obrErrorOccurred")} ${translate("obrContactLibrarian")}`
        );
      }
    } catch (e) {
      console.error(e);
      setError(
        `${translate("systemNotResponding")} ${translate(
          "obrContactLibrarian"
        )}`
      );
    }
  };

  return (
    <React.Fragment>
      {!success && (
        <Container>
          <Toolbar>
            {!isAuthenticated && (
              <Typography variant="h6">
                {translate("obrDialogTitle")}
              </Typography>
            )}
            {isAuthenticated && (
              <Typography variant="h6">
                {translate("obrDialogAddDependantTitle")}
              </Typography>
            )}
          </Toolbar>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack gap={2} p={2}>
                {generalMessage && (
                  <Alert
                    severity="info"
                    children={
                      <div
                        style={{ whiteSpace: "pre-wrap" }}
                        dangerouslySetInnerHTML={generalMessage}
                      ></div>
                    }
                  />
                )}
                <SignUpWithGoogle googleId={googleId} />
                {visibleFields.get("TITLE") && (
                  <FormTextField<OBRFormInput>
                    name="patron.salutation"
                    label={translate("ci_salutation")}
                    required={requiredFields.get("TITLE")}
                    maxLength={20}
                  ></FormTextField>
                )}
                <Stack direction={{ xs: "column", md: "row" }} gap={1}>
                  <FormTextField<OBRFormInput>
                    sx={{ flexGrow: 1 }}
                    label={translate("ci_firstName")}
                    required
                    name="patron.firstName"
                    maxLength={50}
                  />
                  {visibleFields.get("MIDDLENAME") && (
                    <FormTextField<OBRFormInput>
                      sx={{ flexGrow: 1 }}
                      label={translate("ci_middleName")}
                      name="patron.middleName"
                      required={requiredFields.get("MIDDLENAME")}
                      maxLength={50}
                    />
                  )}
                  <FormTextField<OBRFormInput>
                    sx={{ flexGrow: 1 }}
                    label={translate("ci_lastName")}
                    name="patron.lastName"
                    required
                    maxLength={50}
                  />
                </Stack>
                {visibleFields.get("SUFFIX") && (
                  <FormTextField<OBRFormInput>
                    label={translate("ci_suffix")}
                    name="patron.nameSuffix"
                    required={requiredFields.get("SUFFIX")}
                    maxLength={50}
                  />
                )}
                {visibleFields.get("GENDER") && (
                  <GenderField<OBRFormInput>
                    name="patron.gender"
                    label={translate("ci_gender")}
                    required={requiredFields.get("GENDER")}
                  ></GenderField>
                )}
                {visibleFields.get("DOB") && (
                  <DateOfBirthField<OBRFormInput>
                    name="patron.dob"
                    label={translate("ci_dob")}
                    required={
                      requiredFields.get("DOB") ||
                      obrConfiguration.howToGeneratePin ===
                        ObrPinGeneration.Birthday
                    }
                  />
                )}
                <HomeLocation<OBRFormInput>
                  name="patron.issuingBranch"
                  obrConfiguration={obrConfiguration}
                  required={requiredFields.get("HOMEBRANCH")}
                ></HomeLocation>

                <NotificationPreferences<OBRFormInput>
                  expiredCardName={"patron.expiredCardNotificationTypes"}
                  friendlyWarningName={
                    "patron.friendlyWarningNotificationTypes"
                  }
                  overdueName={"patron.overdueNotificationTypes"}
                  finalOverdueName={"patron.finalOverdueNotificationTypes"}
                  arrivedHoldName={"patron.arrivedHoldNotificationTypes"}
                  cancelledHoldName={"patron.cancelledHoldNotificationTypes"}
                  automaticRenewalName={
                    "patron.automaticRenewalNotificationTypes"
                  }
                  hidden={!visibleFields.get("NOTIFICATIONTYPE")}
                  issuingLocation={issuingBranch}
                ></NotificationPreferences>
                {googleId && (
                  <Box sx={{ maxWidth: "320px" }}>
                    <FormControl>
                      <FormLabel>{translate("ci_email")}</FormLabel>
                      <LinkedGoogleAccount
                        email={googleEmail}
                        onClickUnlink={() => {
                          // Create a new object from the current search parameters
                          const newParams = new URLSearchParams(searchParams);

                          // Remove sign in with google search params
                          newParams.delete("googleId");
                          newParams.delete("firstName");
                          newParams.delete("lastName");
                          newParams.delete("email");

                          // Update the search parameters
                          setSearchParams(newParams, { replace: true });

                          // clear the googleId and googleEmail fields
                          // for some reason resetField("patron.googleEmail", {defaultValue: ""}) doesn't work
                          setValue("patron.googleEmail", null);
                          setValue("patron.googleId", null);
                        }}
                      />
                      <FormHelperText> </FormHelperText>
                    </FormControl>
                  </Box>
                )}
                {!googleId && (
                  <PrimaryEmail<OBRFormInput>
                    name={"patron.email"}
                    required={requiredFields.get("EMAIL")}
                    hidden={!visibleFields.get("EMAIL")}
                    confirmEmail="confirmEmail"
                    email2={"patron.email2"}
                    email3={"patron.email3"}
                    notificationTypes={notificationTypes}
                  />
                )}
                <AdditionalEmailGroup<OBRFormInput>
                  email2={visibleFields.get("EMAIL2") ? "patron.email2" : null}
                  email3={visibleFields.get("EMAIL3") ? "patron.email3" : null}
                />

                <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                  <PrimaryPhone<OBRFormInput>
                    name={"patron.phone"}
                    required={
                      requiredFields.get("PHONE1") ||
                      obrConfiguration.howToGeneratePin ===
                        ObrPinGeneration.Last4OfPhone
                    }
                    hidden={!visibleFields.get("PHONE1")}
                    notificationTypes={notificationTypes}
                  />
                  {visibleFields.get("PHONE2") && (
                    <PhoneField<OBRFormInput>
                      sx={{ flexGrow: 1 }}
                      label={translate("ci_altPhoneNumber")}
                      name="patron.altPhone"
                      required={requiredFields.get("PHONE2")}
                    />
                  )}
                </Stack>

                <MobilePhone<OBRFormInput>
                  number={"patron.smsPhone"}
                  required={requiredFields.get("MOBILEPHONE")}
                  hidden={!visibleFields.get("MOBILEPHONE")}
                  carrier={"patron.smsGatewayId"}
                  notificationTypes={notificationTypes}
                  smsGateways={smsGateways}
                />

                {(visibleFields.get("ADDRESS") ||
                  visibleFields.get("CITY") ||
                  visibleFields.get("STATE") ||
                  visibleFields.get("ZIPCODE")) && (
                  <>
                    <Divider sx={{ mb: 3 }} />
                    <AddressFieldGroup<OBRFormInput>
                      line1={"patron.address1"}
                      city={"patron.city"}
                      state={"patron.state"}
                      zip={"patron.zipcode"}
                      hidden={{
                        line1: !visibleFields.get("ADDRESS"),
                        city: !visibleFields.get("CITY"),
                        state: !visibleFields.get("STATE"),
                        zip: !visibleFields.get("ZIPCODE"),
                      }}
                      required={{
                        line1: requiredFields.get("ADDRESS"),
                        city: requiredFields.get("CITY"),
                        state: requiredFields.get("STATE"),
                        zip: requiredFields.get("ZIPCODE"),
                      }}
                      notificationTypes={notificationTypes}
                    />
                  </>
                )}

                {(visibleFields.get("ADDRESS2") ||
                  visibleFields.get("CITY2") ||
                  visibleFields.get("STATE2") ||
                  visibleFields.get("ZIPCODE2")) && (
                  <>
                    <Divider sx={{ mb: 3 }} />
                    <AddressFieldGroup<OBRFormInput>
                      line1={"patron.secondaryAddress1"}
                      city={"patron.secondaryCity"}
                      state={"patron.secondaryState"}
                      zip={"patron.secondaryZipcode"}
                      hidden={{
                        line1: !visibleFields.get("ADDRESS2"),
                        city: !visibleFields.get("CITY2"),
                        state: !visibleFields.get("STATE2"),
                        zip: !visibleFields.get("ZIPCODE2"),
                      }}
                      required={{
                        line1: requiredFields.get("ADDRESS2"),
                        city: requiredFields.get("CITY2"),
                        state: requiredFields.get("STATE2"),
                        zip: requiredFields.get("ZIPCODE2"),
                      }}
                      labels={{
                        line1: translate("ci_address2"),
                        city: translate("ci_city2"),
                        state: translate("ci_state2"),
                        zip: translate("ci_zipCode2"),
                      }}
                    />
                  </>
                )}
                {obrConfiguration &&
                  obrConfiguration.borrowerFields.map((borrowerFieldConfig) => {
                    return (
                      <BorrowerFieldControl
                        key={borrowerFieldConfig.fieldNumber}
                        configuration={borrowerFieldConfig}
                      ></BorrowerFieldControl>
                    );
                  })}
                <Divider sx={{ mb: 3 }} />
                <PinField<OBRFormInput>
                  name="patron.pin"
                  confirmName="confirmPIN"
                  obrConfiguration={obrConfiguration}
                ></PinField>
                {termsAndConditions && (
                  <TermsAndConditions<OBRFormInput>
                    termsAndConditions={termsAndConditions}
                    name="agreeToTAC"
                  ></TermsAndConditions>
                )}
                {error && <Alert severity="error">{error}</Alert>}
                <ObrUrl />
                <Stack direction="row" gap={2}>
                  <Button variant="contained" type="submit">
                    {translate("register")}
                  </Button>
                  <Button
                    variant="text"
                    type="button"
                    href={isAuthenticated ? "/#/myaccount/linked" : "/#/"}
                  >
                    {translate("cancel")}
                  </Button>
                </Stack>
              </Stack>
            </form>
            {/* <DevTool control={control} /> */}
          </FormProvider>
        </Container>
      )}

      {success && (
        <OBRSuccess
          response={success.response}
          responseHTML={success.responseHTML}
        ></OBRSuccess>
      )}
    </React.Fragment>
  );
};
